import { useQuery } from '@tanstack/react-query';
import { api, type ApiError, type GetForecastAccuracyResult } from '@whinerp/client';
import { ErrorState, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount, formatDate, formatMoney } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * Forecast against actual (G-144, F-102).
 *
 * The chart is easy and the honesty is the work. Three things this panel says
 * about its own figures, each drawn only when it applies:
 *
 * The period has not ended, so every forecast is being compared against what
 * has closed SO FAR. Without that line the report makes everybody look wildly
 * optimistic on the screen where their judgement is assessed.
 *
 * Some weeks were never captured. Their absence is not a zero, and a chart
 * that plotted zero for the months before the feature existed would be saying
 * the team forecast nothing all year.
 *
 * Some weeks nobody had forecast yet. That is not a week they were wrong by
 * the whole quarter.
 */
export function ForecastAccuracyPanel({
  periodStart,
  periodEnd,
  periodLabel,
}: {
  periodStart: string;
  periodEnd: string;
  periodLabel: string;
}): JSX.Element | null {
  const t = useT();
  const format = useFormat();
  const { can } = useSession();

  const accuracy = useQuery<GetForecastAccuracyResult, ApiError>({
    queryKey: ['forecast-accuracy', periodStart, periodEnd],
    queryFn: () => api.getForecastAccuracy({ query: { periodStart, periodEnd } }),
    enabled: can('forecast.view.team'),
  });

  if (!can('forecast.view.team')) return null;
  if (accuracy.isPending) return <Skeleton className="h-block-md w-full" />;
  if (accuracy.isError) {
    return (
      <ErrorState
        title={t('state.error')}
        body={accuracy.error.message}
        onRetry={() => void accuracy.refetch()}
      />
    );
  }

  const data = accuracy.data;
  const money = (amount: string): string => formatMoney(amount, format, { currency: data.currency });
  const weeks = [...new Set(data.rows.flatMap((row) => row.weeks.map((week) => week.weekStart)))].sort();

  return (
    <Panel data-region="forecast-accuracy">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{`${t('accuracy.title')} — ${periodLabel}`}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('accuracy.body')}</p>
        </div>

        {/* Drawn only while the period is running. A permanent caveat is one
            nobody reads, and this one has to be read. */}
        {data.periodComplete ? null : (
          <p
            data-accuracy-partial
            className="rounded-control bg-warning-tint px-3 py-2 text-dense text-warning-text"
          >
            {t('accuracy.partial')}
          </p>
        )}
        {data.weeksNotCaptured.length > 0 ? (
          <p data-accuracy-gaps className="rounded-control bg-sunken px-3 py-2 text-dense text-ink-meta">
            {t('accuracy.gaps', {
              values: { count: formatCount(data.weeksNotCaptured.length, format) },
            })}
          </p>
        ) : null}

        {data.rows.length === 0 ? (
          <p data-accuracy-empty className="text-meta text-ink-meta">
            {t('accuracy.empty')}
          </p>
        ) : (
          <div data-scroll-container className="overflow-x-auto">
            <table className="w-full min-w-block-xl border-collapse text-body">
              <thead>
                <tr>
                  <th
                    scope="col"
                    className="border-b border-b-rule py-2 text-start text-micro uppercase tracking-label text-ink-label"
                  >
                    {t('target.subject.user')}
                  </th>
                  {weeks.map((week) => (
                    <th
                      key={week}
                      scope="col"
                      data-accuracy-week={week}
                      className="border-b border-b-rule py-2 text-end text-micro uppercase tracking-label text-ink-label"
                    >
                      {t('accuracy.week', { values: { date: formatDate(week, format) } })}
                    </th>
                  ))}
                  <th
                    scope="col"
                    className="border-b border-b-rule py-2 text-end text-micro uppercase tracking-label text-ink-label"
                  >
                    {t('accuracy.actual')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((row) => (
                  <tr key={row.userId} data-accuracy-row={row.userId}>
                    <th scope="row" className="border-b border-b-rule py-2 text-start font-medium text-ink">
                      {row.name}
                    </th>
                    {weeks.map((week) => {
                      const found = row.weeks.find((entry) => entry.weekStart === week);
                      const short = found?.error !== null && found?.error !== undefined
                        ? found.error.startsWith('-')
                        : false;
                      return (
                        <td
                          key={week}
                          data-accuracy-cell={`${row.userId}|${week}`}
                          className="border-b border-b-rule py-2 text-end align-top"
                        >
                          {found === undefined || found.effective === null ? (
                            /* Not forecast that week, which is not a week they
                               were wrong by the whole quarter. */
                            <span
                              data-accuracy-not-forecast={`${row.userId}|${week}`}
                              className="text-meta text-ink-meta"
                            >
                              {t('accuracy.error.none')}
                            </span>
                          ) : (
                            <div className="flex flex-col items-end">
                              <Num className="text-ink">{money(found.effective)}</Num>
                              <span
                                data-accuracy-error={`${row.userId}|${week}`}
                                className={short ? 'text-meta text-ink-meta' : 'text-meta text-warning-text'}
                              >
                                {short
                                  ? t('accuracy.error.under', {
                                      values: { amount: money(found.error!.slice(1)) },
                                    })
                                  : t('accuracy.error.over', { values: { amount: money(found.error!) } })}
                              </span>
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td data-accuracy-actual={row.userId} className="border-b border-b-rule py-2 text-end">
                      <Num className="text-ink">{money(row.actual)}</Num>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Panel>
  );
}
